import { makeStyles, mergeClasses } from "@fluentui/react-components";
/**
 * SuggestionChips — context actions above the CommandRail input. Every
 * chip runs a real action on what the writer is pointing at; there are no
 * canned chat prompts.
 *
 * While text is highlighted (``selectionText`` non-null):
 *   - "Resolve missing info" — only when the highlight contains a fill-in
 *     placeholder (``[MISSING INFO: …]``, ``[FILL: …]``, ``[PENDING…]``,
 *     ``[NOT FOUND…]``). Runs the highlight-edit flow with the
 *     Issues-tab resolve instruction → before/after card.
 *   - "Ask Asthra to change" — pins the composer to the highlight-edit flow.
 *   - "Replace with text from a source" — any section or table of any source
 *     document, word for word, in place of the highlight (reviewed first).
 *   - "View citations" — citations for the highlighted text.
 *   (The highlighted text itself shows in the selection pill below.)
 *
 * Otherwise (cursor in the document, nothing highlighted):
 *   - "Regenerate this section" — the Spine Inspector's Regenerate dialog
 *     (optional steering instructions) for the cursor section.
 *   - "Insert from source" — copy the cursor section (or a table) word for
 *     word from a source document; the change goes to review.
 *   - "Resolve flags" — every open flag in the section → the Issues tab's
 *     Resolve-issues wizard (bulk resolve, fixes land as proposals).
 *   - "See all flags" — the section's Issues tab in the Inspector.
 *
 * "Replace Image" shows whenever an image is selected.
 *
 * The section chips cannot name the section: the add-in keeps no ambient
 * cursor read (it froze Word on long drafts), so the section resolves at
 * click time and the resulting card / error names it.
 *
 * # DECISION (2026-09-21): replaced the six canned chat-prompt chips
 * #   ("What sources were used?", "Explain this section", "Compare with
 * #   source", "Find newer evidence", "Flag for review", "Summarise the
 * #   document").
 * # WHY: largely unused — each sent a fixed prompt to chat instead of
 * #      acting on the cursor context, and "Flag for review" created no
 * #      real flags. The replacements map one-to-one onto existing flows.
 *
 * SRP: pure presentation. Visibility is decided from props; dispatch and
 * the selection probe live in the rail/shell.
 */
import { countMissingInfoFlags } from "../services/missingInfoProgress";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface SuggestionChipsProps {
  /** Text of the writer's chip-eligible Word highlight, or null when
   *  nothing (or too much) is highlighted. */
  selectionText: string | null;
  /** Citations fetch for the current selection. Omitted → no chip. */
  onCite?: () => void;
  /** Disables every chip (input not live). */
  disabled: boolean;
  /** Disables the edit/redraft chips only — set while an edit or its
   *  proposal is in flight so a second change can't stack. */
  actionsDisabled?: boolean;
  /** When true, an image is currently selected in the document. */
  imageSelected?: boolean;
  /** Replace Image chip handler (navigates to the Images tab). */
  onReplaceImage?: () => void;
  /** "Resolve missing info" handler. Omitted → no chip. */
  onResolveMissingInfo?: () => void;
  /** "Ask Asthra to change" handler. Omitted → no chip. */
  onAskToChange?: () => void;
  /** "Replace with text from a source" (the highlight) handler. Omitted → no chip. */
  onReplaceFromSource?: () => void;
  /** "Regenerate this section" handler. Omitted → no chip. */
  onRegenerateSection?: () => void;
  /** "Insert from source" (cursor section) handler. Omitted → no chip. */
  onInsertFromSource?: () => void;
  /** "Resolve flags" (cursor section) handler. Omitted → no chip. */
  onResolveSectionFlags?: () => void;
  /** "See all flags" (cursor section) handler. Omitted → no chip. */
  onSeeSectionFlags?: () => void;
  /** Key of the chip whose action is starting (e.g. "regen"). That chip
   *  shows a spinner + "Finding your section…" instead of its label: the
   *  click-time section read can take a few seconds on a long document. */
  pendingChip?: string | null;
}

/** Loading copy for a starting chip. Section chips first ask Word which
 *  section the cursor is in; "Resolve missing info" goes straight to the
 *  edit. Exported for tests. */
export function pendingChipLabel(key: string): string {
  return key === "resolve" ? "Starting…" : "Finding your section…";
}

/** Number of fill-in placeholder markers (``[MISSING INFO: …]``, ``[FILL: …]``,
 *  ``[PENDING…]``, ``[NOT FOUND…]``) in the highlight — "Resolve missing info"
 *  only shows when this is > 0, and shows the count.
 *  Exported for tests. */
export function countMissingInfoMarkers(text: string): number {
  return countMissingInfoFlags(text);
}

/** True when the highlight contains at least one missing-info marker.
 *  Exported for tests. */
export function hasMissingInfoMarker(text: string): boolean {
  return countMissingInfoMarkers(text) > 0;
}

// ---------------------------------------------------------------------------
// Styles — follows the "Asthra Conversation Rail" design: white pills,
// 1px lineStrong border, 14px radius, Inter 11px in brand purple.
// ---------------------------------------------------------------------------

// Missing-info amber — the same warm palette the design uses for gap
// call-outs, so the resolve action reads as "about the flags".
const AMBER = { bg: "#FFFBEB", bgHover: "#FEF3C7", line: "#FCD34D", lineHover: "#F59E0B", ink: "#92400E" };

const useStyles = makeStyles({
  // Inset to line up with the selection pill and the input row below.
  root: {
    padding: "6px 12px 4px",
    fontFamily: fonts.body,
    animationName: {
      from: { opacity: 0, transform: "translateY(3px)" },
      to: { opacity: 1, transform: "none" },
    },
    animationDuration: "160ms",
    animationTimingFunction: "ease-out",
    "@media (prefers-reduced-motion: reduce)": { animationName: "none" },
  },
  // Own line, so wrapped chips align under each other, not under the label.
  // Small uppercase mono meta — the film's label register.
  caption: {
    marginBottom: "6px",
    fontFamily: fonts.mono,
    fontSize: "9.5px",
    fontWeight: 600,
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    color: inkColors.ink4,
    userSelect: "none",
  },
  // A native <fieldset> groups the chips for assistive tech; reset its
  // default border / padding / min-width so it lays out like a plain row.
  chips: {
    border: "none",
    margin: 0,
    padding: 0,
    minWidth: 0,
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "6px",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    flexShrink: 0,
    padding: "4px 11px 4px 9px",
    borderRadius: "14px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    background: "#FFFFFF",
    color: brandColors.primary,
    fontFamily: fonts.body,
    fontSize: "11px",
    fontWeight: 500,
    lineHeight: "16px",
    whiteSpace: "nowrap",
    cursor: "pointer",
    boxShadow: "0 1px 1px rgba(15, 10, 30, 0.04)",
    transitionProperty: "background-color, border-color, box-shadow, transform, color",
    transitionDuration: "120ms",
    transitionTimingFunction: "ease-out",
    "&:hover:not(:disabled)": {
      background: brandColors.tint,
      border: `1px solid ${brandColors.primary}66`,
      boxShadow: `0 2px 6px ${brandColors.primary}1F`,
      transform: "translateY(-1px)",
    },
    "&:active:not(:disabled)": {
      transform: "none",
      boxShadow: "none",
    },
    "&:focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "1px",
    },
    "&:disabled": {
      cursor: "default",
      opacity: 0.45,
      boxShadow: "none",
    },
    "@media (prefers-reduced-motion: reduce)": { transitionDuration: "0ms" },
  },
  // Lookups and the whole-section redraft: quieter ink so the edit actions
  // lead. The redraft replaces the entire section (as a proposal), so it
  // should never be the loudest thing in the row.
  // "Ask Asthra to change" — the film's filled "✦ Ask Asthra" pill: the
  // one primary action for a highlight, so it is the only filled chip.
  primary: {
    background: brandColors.primary,
    border: `1px solid ${brandColors.primary}`,
    color: "#FFFFFF",
    fontWeight: 600,
    boxShadow: `0 1px 4px ${brandColors.primary}33`,
    "&:hover:not(:disabled)": {
      background: brandColors.primary,
      border: `1px solid ${brandColors.primary}`,
      boxShadow: `0 3px 10px ${brandColors.primary}40`,
    },
  },
  quiet: {
    color: inkColors.ink3,
    "&:hover:not(:disabled)": { color: brandColors.primary },
  },
  amber: {
    background: AMBER.bg,
    border: `1px solid ${AMBER.line}`,
    color: AMBER.ink,
    "&:hover:not(:disabled)": {
      background: AMBER.bgHover,
      border: `1px solid ${AMBER.lineHover}`,
      boxShadow: "0 2px 6px rgba(245, 158, 11, 0.18)",
    },
    "&:focus-visible": { outline: `2px solid ${AMBER.lineHover}` },
  },
  // The starting chip stays at full strength (it is disabled while it
  // works, but must not look dead) with a progress cursor.
  busy: {
    "&:disabled": { opacity: 1, cursor: "progress" },
  },
  // Loading glyph on the starting chip.
  spinner: {
    animationName: { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
    animationDuration: "900ms",
    animationTimingFunction: "linear",
    animationIterationCount: "infinite",
    "@media (prefers-reduced-motion: reduce)": { animationName: "none" },
  },
  icon: {
    display: "inline-flex",
    justifyContent: "center",
    width: "12px",
    fontSize: "11px",
    lineHeight: 1,
    opacity: 0.85,
  },
  count: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "16px",
    height: "16px",
    padding: "0 5px",
    marginLeft: "1px",
    borderRadius: "8px",
    background: "#FDE68A",
    color: AMBER.ink,
    fontSize: "10px",
    fontWeight: 700,
    boxSizing: "border-box",
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ChipSpec {
  key: string;
  label: string;
  icon: string;
  onClick: () => void;
  tone: "brand" | "primary" | "quiet" | "amber";
  disabled: boolean;
  /** Trailing count badge (missing-info markers). */
  count?: number;
}

const SuggestionChips = ({
  selectionText,
  onCite,
  disabled,
  actionsDisabled,
  imageSelected,
  onReplaceImage,
  onResolveMissingInfo,
  onAskToChange,
  onReplaceFromSource,
  onRegenerateSection,
  onInsertFromSource,
  onResolveSectionFlags,
  onSeeSectionFlags,
  pendingChip,
}: SuggestionChipsProps) => {
  const styles = useStyles();
  const actionOff = disabled || actionsDisabled === true;
  const chips: ChipSpec[] = [];

  if (selectionText) {
    const flags = countMissingInfoMarkers(selectionText);
    if (onResolveMissingInfo && flags > 0) {
      chips.push({
        key: "resolve",
        label: "Resolve missing info",
        icon: "⚑",
        onClick: onResolveMissingInfo,
        tone: "amber",
        disabled: actionOff,
        count: flags,
      });
    }
    if (onAskToChange) {
      chips.push({
        key: "change",
        label: "Rewrite this text",
        icon: "✦",
        onClick: onAskToChange,
        tone: "primary",
        disabled: actionOff,
      });
    }
    if (onReplaceFromSource) {
      chips.push({
        key: "replace-source",
        label: "Replace with text from a source",
        icon: "⇣",
        onClick: onReplaceFromSource,
        tone: "quiet",
        disabled: actionOff,
      });
    }
    if (onCite) {
      chips.push({ key: "cite", label: "View citations", icon: "❝", onClick: onCite, tone: "quiet", disabled });
    }
  }
  if (imageSelected && onReplaceImage) {
    chips.push({
      key: "image",
      label: "Replace Image",
      icon: "▣",
      onClick: onReplaceImage,
      tone: "brand",
      disabled,
    });
  }
  if (!selectionText) {
    if (onRegenerateSection) {
      chips.push({
        key: "regen",
        label: "Regenerate this section",
        icon: "↻",
        onClick: onRegenerateSection,
        tone: "quiet",
        disabled: actionOff,
      });
    }
    if (onInsertFromSource) {
      chips.push({
        key: "insert-source",
        label: "Insert from source",
        icon: "⇣",
        onClick: onInsertFromSource,
        tone: "quiet",
        disabled: actionOff,
      });
    }
    if (onResolveSectionFlags) {
      chips.push({
        key: "resolve-section",
        label: "Fix flags",
        icon: "⚑",
        onClick: onResolveSectionFlags,
        tone: "amber",
        disabled: actionOff,
      });
    }
    if (onSeeSectionFlags) {
      // A lookup — stays clickable while an edit is in flight.
      chips.push({
        key: "see-flags",
        label: "See all flags",
        icon: "☰",
        onClick: onSeeSectionFlags,
        tone: "quiet",
        disabled,
      });
    }
  }

  if (chips.length === 0) return null;

  const mode = selectionText ? "highlight" : "section";
  return (
    // Keyed on the mode so the entry animation replays when the set swaps.
    <div key={mode} className={styles.root}>
      <div className={styles.caption} aria-hidden="true">
        {selectionText ? "For the highlighted text" : "For this section"}
      </div>
      <fieldset
        className={styles.chips}
        aria-label={selectionText ? "Actions for the highlighted text" : "Actions for this section"}
      >
        {chips.map((c) => {
          const pending = pendingChip === c.key;
          const label = pending ? pendingChipLabel(c.key) : c.label;
          return (
            <button
              key={c.key}
              type="button"
              disabled={c.disabled}
              onClick={c.onClick}
              aria-busy={pending || undefined}
              aria-label={
                !pending && c.count ? `${c.label} (${c.count} ${c.count === 1 ? "flag" : "flags"})` : undefined
              }
              className={mergeClasses(
                styles.chip,
                c.tone === "primary" && styles.primary,
                c.tone === "quiet" && styles.quiet,
                c.tone === "amber" && styles.amber,
                pending && styles.busy,
              )}
            >
              <span className={mergeClasses(styles.icon, pending && styles.spinner)} aria-hidden="true">
                {pending ? "◌" : c.icon}
              </span>
              {label}
              {!pending && c.count ? (
                <span className={styles.count} aria-hidden="true">
                  {c.count}
                </span>
              ) : null}
            </button>
          );
        })}
      </fieldset>
    </div>
  );
};

export default SuggestionChips;
